import { cn } from '../../lib/cn';
import { SegmentedBar } from '../primitives/ProgressBar';
import { Button } from '../primitives/Button';
import { PlusIcon } from '../../icons/ui-icons';
import type { ReactNode } from 'react';

export type TalentPathId = 'economy' | 'war' | 'astronomy';

export interface TalentTier {
  /** Inclusive level span this tier covers, e.g. [1, 5]. */
  range: [number, number];
  /** Effect description at full investment. */
  effect: string;
}

export interface TalentPathProps {
  path: TalentPathId;
  name: ReactNode;
  icon?: ReactNode;
  /** Points already invested in this path. */
  invested: number;
  /** Maximum points the path accepts (15 in the shipped tree). */
  max?: number;
  tiers: TalentTier[];
  /** Unspent points the player holds — gates the invest button. */
  availablePoints?: number;
  onInvest?: () => void;
  className?: string;
}

const PATH_ACCENT = {
  economy: 'credit',
  war: 'danger',
  astronomy: 'isotope',
} as const;

const PATH_RING = {
  economy: 'border-credit/40 bg-credit/[0.06]',
  war: 'border-danger/40 bg-danger/[0.06]',
  astronomy: 'border-isotope/40 bg-isotope/[0.06]',
} as const;

const PATH_TEXT = {
  economy: 'text-credit-bright',
  war: 'text-danger-bright',
  astronomy: 'text-isotope-bright',
} as const;

/**
 * One branch of the three-way talent tree (GDD §2).
 *
 * Tiers unlock in bands of five points, so the tier list highlights the band
 * the player is currently filling — that's the only tier whose wording changes
 * with the next point spent, and it's what they're deciding about.
 */
export function TalentPath({
  path,
  name,
  icon,
  invested,
  max = 15,
  tiers,
  availablePoints = 0,
  onInvest,
  className,
}: TalentPathProps) {
  const canInvest = availablePoints > 0 && invested < max;

  return (
    <div className={cn('rounded-panel border bg-plate p-3', PATH_RING[path], className)}>
      <div className="flex items-center gap-2.5">
        {icon ? <span className={cn('shrink-0', PATH_TEXT[path])}>{icon}</span> : null}
        <div className="min-w-0 flex-1">
          {/* Wraps rather than truncates: "Astronomie & Defensive" losing its
              second word to an ellipsis costs more than a second line does. */}
          <h4
            className={cn(
              'font-display text-xs font-semibold uppercase leading-tight tracking-[0.1em]',
              PATH_TEXT[path],
            )}
          >
            {name}
          </h4>
        </div>
        <span data-sg-numeric className="shrink-0 text-label text-content-secondary">
          {invested}/{max}
        </span>
      </div>

      <SegmentedBar
        value={invested}
        max={max}
        accent={PATH_ACCENT[path]}
        className="mt-2.5"
      />

      <ul className="mt-3 space-y-1.5">
        {tiers.map((tier) => {
          const [from, to] = tier.range;
          const complete = invested >= to;
          const current = invested >= from - 1 && invested < to;

          return (
            <li
              key={`${from}-${to}`}
              className={cn(
                'flex gap-2 rounded-control px-2 py-1.5 text-xs leading-relaxed',
                current && 'bg-surface-3',
                complete ? 'text-content-secondary' : 'text-content-muted',
                !complete && !current && 'opacity-60',
              )}
            >
              <span
                data-sg-numeric
                className={cn('shrink-0 font-display text-label-sm', complete && PATH_TEXT[path])}
              >
                {from}–{to}
              </span>
              <span>{tier.effect}</span>
            </li>
          );
        })}
      </ul>

      {onInvest ? (
        <Button
          variant="secondary"
          size="sm"
          fullWidth
          className="mt-3"
          disabled={!canInvest}
          onClick={onInvest}
          icon={<PlusIcon size={13} />}
        >
          {invested >= max ? 'Maximum erreicht' : 'Punkt einsetzen'}
        </Button>
      ) : null}
    </div>
  );
}
